
const state = () => ({
  /**
   * This contains the item that the user is adding to the order or editing.
   * @type Object
   */
  item: {},

  /**
   * Determine if the user is adding an item to the order or editing it.
   * @type Boolean
   */
  editing: false,

  /**
   * This variable contains all the Modifier Categories with their modifiers.
   * @type Array
   */
  categories: [],

  /**
   * This is the v-stepper component actual value.
   * @type Number
   */
  step: 1,
})

const getters = {
  _successful: state => category => category.min === 0
    ? true
    : category.failed
      ? false
      : category.modifiers.some(modifier => modifier.selected)
  ,
  _modifiers: state => state.categories.flatMap(category => category.modifiers),
  selected: (state, getters) => getters._modifiers.filter(modifier => modifier.selected),
  hidden: (state, getters, rootState, rootGetters) => item => rootGetters['catalog/hiddenModifierCategories'](item ?? state.item)
    .flatMap(category => rootGetters['catalog/modifiers'](category.id)
      .filter(modifier => !! modifier.autoSelected)
    )
  ,
  isNotLastStep: state => step => step < state.categories.length,
  title: (state, getters, rootState, rootGetters) => rootGetters['catalog/modifierGroup'](state.item)?.name ?? '',
}

const mutations = {
  _item (state, v) {
    state.item = v
  },
  _editing (state, v) {
    state.editing = Boolean(v)
  },
  _categories (state, v) {
    state.categories = v
  },
  step (state, v) {
    state.step = v
  },
  nextStep (state) {
    state.step++
  },
  complete (state, { index, value }) {
    if (! state.editing) {
      state.categories[index].completed = value ?? true
    }
  },
  fail (state, { index, value }) {
    state.categories[index].failed = value ?? true
  },
  select (state, { index, value }) {
    const category = state.categories[index]
    const selected = category.max > 1 ? value : [value]
    category.modifiers.forEach(modifier => {
      modifier.selected = selected.indexOf(modifier.id) !== -1
    })
  },
}

const actions = {
  init ({ commit, dispatch, rootGetters }, payload) {
    commit('_item', payload.item)
    commit('_editing', payload.editing)
    // A 'menu' may override the item's modifier categories.
    const categories = payload.group_id
      ? rootGetters['catalog/modifierGroupCategories'](payload.group_id)
      : rootGetters['catalog/visibleModifierCategories'](payload.item)
    commit('_categories', categories
      .map((category, index) => ({
        ...category,
        index,
        completed: false,
        failed: false,
        modifiers: rootGetters['catalog/modifiers'](category.id)
          .map(modifier => ({
            ...modifier,
            price: 'menuPivot' in payload.item && payload.item.menuPivot.addModifiersPrice === false ? 0 : parseFloat(modifier.price),
            recalculatePrice: !! payload.item.menuPivot?.addModifiersPrice,
            selected: Boolean(payload.editing
              ? payload.modifiers.filter(m => m.category_id == category.id).pluck('id').includes(modifier.id)
              : !! modifier.autoSelected
            ),
          }))
        ,
      }))
    )
    commit('step', 1)
  },
  validate ({ state, commit, getters }, mark = false) {
    const unsuccessful = state.categories.filter(o => ! getters._successful(o))
    if (unsuccessful.length === 0) {
      return true
    }
    if (mark) {
      unsuccessful.forEach(o => commit('fail', { index: o.index }))
      commit('step', unsuccessful[0].index + 1)
    }
    return false
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
